/**
 * 页面场景 → 军师技能推荐 hint(纯前端,零契约改动)。
 *
 * <p>★安全界定:此表只对 `GET /advisor/skills` **已下发**的卡做**重排**,永不新增、永不解锁。
 * 后端已过 role / overlay / 租户 disabled / BuiltinSkillCatalog.HIDDEN 四道闸;此处 skill 名 join
 * 不上就自然失效(如 assign-task / biz-diagnosis 当前在 HIDDEN 集,后端不回 → hint 静默无效,
 * 哪天恢复上架前端零改动自动生效)。
 *
 * <p>skill 名 = ai 侧 builtin 目录名,须与 api 侧 BuiltinSkillCatalog.ALL 对齐。
 */

export type SceneKey =
  | "poster"
  | "video"
  | "digital_human"
  | "customer"
  | "coaching"
  | "geo"
  | "knowledge"
  | "growth";

export interface Scene {
  key: SceneKey;
  /** 展示用中文名(「你在 AI海报 · 已把相关技能置顶」)。 */
  label: string;
  /** 命中该场景的路由前缀(按路径段匹配)。 */
  paths: string[];
  /** 置顶 skill,按优先级排;取前若干进首屏。 */
  skills: string[];
}

export const SCENES: readonly Scene[] = [
  {
    key: "poster",
    label: "视觉舱",
    paths: ["/ai-haibao"],
    skills: ["product-poster", "edit-image", "brand-stamp"],
  },
  { key: "video", label: "视频舱", paths: ["/ai-video"], skills: ["promo-video", "write-copy"] },
  {
    key: "digital_human",
    label: "分身舱",
    paths: ["/ai-digital-human"],
    skills: ["digital-human", "write-copy"],
  },
  {
    key: "customer",
    label: "客服舱",
    paths: ["/ai-kefu", "/channels"],
    skills: ["cs-insight", "write-copy"],
  },
  {
    key: "coaching",
    label: "带教陪练",
    // 注意:只收带队相关的 /advisor-*,军师自身管理页(conversations/skills/memory)不算场景。
    paths: ["/ai-peilian", "/team-tasks", "/advisor-dispatches"],
    skills: ["dispatch-coaching", "assign-task"],
  },
  { key: "geo", label: "GEO雷达", paths: ["/geo-radar"], skills: ["geo-probe"] },
  {
    key: "knowledge",
    label: "知识库",
    paths: ["/knowledge", "/products"],
    skills: ["write-copy", "content-plan"],
  },
  {
    key: "growth",
    label: "经营看板",
    // 2026-07-30 四合一:`/growth-cockpit` 与 `/advisor-team-board` 都成了重定向,场景挂它们等于死数据。
    // 整个 `/advisor` 页(看盘/记忆/技能/两项审计)归「经营增长」这一个场景 —— 场景只认 pathname,
    // 分不到 tab 粒度;MANAGER 原来在团队看板拿的是带教技能,现在统一成经营技能,这是有意的收敛。
    // 段边界匹配保证 `/advisor` 不会误吃 `/advisor-dispatches`(见 matchScene)。
    paths: ["/advisor"],
    skills: ["biz-diagnosis", "content-plan"],
  },
];

/** 路由前缀 → scene 的扁平查找表,长前缀在前(最长前缀优先)。 */
const INDEX: { path: string; scene: Scene }[] = SCENES.flatMap((scene) =>
  scene.paths.map((path) => ({ path, scene }))
).sort((a, b) => b.path.length - a.path.length);

/**
 * 命中当前路由的场景;无场景(工作台/设置等)→ null(网格回落全局 recommended,行为不变)。
 *
 * <p>按**路径段边界**匹配而非裸 startsWith:否则 `/products` 会吃掉 `/products-archive`、
 * `/advisor-team-board` 的前缀会误伤 `/advisor-conversations` 这类同前缀的管理页。
 */
export function matchScene(pathname: string): Scene | null {
  if (!pathname) return null;
  const path = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  const hit = INDEX.find(({ path: p }) => path === p || path.startsWith(`${p}/`));
  return hit ? hit.scene : null;
}
